// Pattern Stamp Tool — stamps patterns instead of brush strokes.
// Shares shortcut S with Clone Stamp, cycles via Shift+S.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { mergeBBox, stampLine, type BBox } from "../pixel/brush";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export class PatternStampTool implements Tool {
  readonly id = "pattern-stamp";
  readonly shortcut = "S";

  /**
   * Pattern to stamp (RGBA pixels).
   *
   * i184 — `name` is optional metadata (the Pattern Editor preset's label,
   * when the caller has one available) so the options bar can show which
   * pattern is currently loaded (e.g. "Pattern: Bricks") next to the
   * preview swatch built from `getPatternPreviewThumbnail()` below, without
   * needing to reopen the Pattern Editor just to check.
   */
  pattern: { pixels: Uint8ClampedArray; width: number; height: number; name?: string } | null = null;
  /** Pattern offset within the stamp. */
  patternOffsetX = 0;
  patternOffsetY = 0;

  size = 30;
  hardness = 0.5;
  opacity = 1;
  flow = 1;
  aligned = false; // if false, pattern restarts at each stroke

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    strokeStartX: number;
    strokeStartY: number;
  } | null = null;

  private getSelMask(ctx: ToolContext): { mask: Uint8Array | undefined; w: number | undefined } {
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    if (!sel || !sel.mask) return { mask: undefined, w: undefined };
    return { mask: sel.mask, w: ctx.doc!.width };
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId || !this.pattern) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x, docY = p.y - layer.y;
    this.active = {
      layer, layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX, lastY: docY, bbox: null,
      strokeStartX: docX, strokeStartY: docY,
    };
    this.stampAt(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x, docY = p.y - this.active.layer.y;
    const dx = docX - this.active.lastX, dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / 2));
    for (let i = 1; i <= n; i++) {
      this.stampAt(ctx, this.active.lastX + dx * (i / n), this.active.lastY + dy * (i / n));
    }
    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }
    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  /** Whether a pattern is currently loaded (options bar swatch visibility). */
  get hasPattern(): boolean {
    return this.pattern !== null;
  }

  /**
   * i184 — produce a small, fixed-size RGBA thumbnail of the currently
   * loaded pattern (nearest-neighbor sampled), for the options bar to
   * render as a preview swatch next to a "change pattern" button — so the
   * currently-loaded pattern is visible without leaving the options bar or
   * reopening the Pattern Editor.
   *
   * Pure pixel math (no Canvas/DOM API): the caller decides how to actually
   * display the returned RGBA buffer (e.g. via ImageData + a small
   * <canvas>), so this stays host-agnostic and independently testable.
   * Returns null when no pattern is loaded.
   */
  getPatternPreviewThumbnail(size = 24): { pixels: Uint8ClampedArray; width: number; height: number } | null {
    if (!this.pattern) return null;
    const safeSize = Math.max(1, Math.floor(size));
    const { pixels, width, height } = this.pattern;
    const out = new Uint8ClampedArray(safeSize * safeSize * 4);
    for (let y = 0; y < safeSize; y++) {
      const srcY = Math.min(height - 1, Math.floor((y * height) / safeSize));
      for (let x = 0; x < safeSize; x++) {
        const srcX = Math.min(width - 1, Math.floor((x * width) / safeSize));
        const srcIdx = (srcY * width + srcX) * 4;
        const dstIdx = (y * safeSize + x) * 4;
        out[dstIdx] = pixels[srcIdx]!;
        out[dstIdx + 1] = pixels[srcIdx + 1]!;
        out[dstIdx + 2] = pixels[srcIdx + 2]!;
        out[dstIdx + 3] = pixels[srcIdx + 3]!;
      }
    }
    return { pixels: out, width: safeSize, height: safeSize };
  }

  private stampAt(ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active || !this.pattern) return;
    const layer = this.active.layer;
    const data = layer.pixels.data;
    const w = layer.pixels.width, h = layer.pixels.height;
    const radius = this.size / 2;
    const patW = this.pattern.width, patH = this.pattern.height;

    // Pattern offset: if aligned, use absolute doc position; otherwise use stroke-relative
    const baseX = this.aligned ? docX : docX - this.active.strokeStartX;
    const baseY = this.aligned ? docY : docY - this.active.strokeStartY;
    const patOffX = ((Math.floor(baseX) + this.patternOffsetX) % patW + patW) % patW;
    const patOffY = ((Math.floor(baseY) + this.patternOffsetY) % patH + patH) % patH;

    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));
    const r2 = radius * radius;
    const hardness = this.hardness;
    const opacity = this.flow;
    const { mask: selMask, w: selMaskW } = this.getSelMask(ctx);
    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy * dy >= r2) continue;
        const dist = Math.sqrt(dx * dx + dy * dy) / radius;
        let strength = (dist >= 1 ? 0 : dist > hardness ? (1 - dist) / (1 - hardness || 1e-6) : 1) * opacity;

        if (selMask && selMaskW) {
          if (selMask[py * selMaskW + px] === 0) continue;
          strength *= selMask[py * selMaskW + px]! / 255;
        }
        if (layer.lockTransparency && data[(py * w + px) * 4 + 3] === 0) continue;

        const idx = (py * w + px) * 4;
        const piX = ((px + patOffX) % patW + patW) % patW;
        const piY = ((py + patOffY) % patH + patH) % patH;
        const pi = (piY * patW + piX) * 4;
        const pr = this.pattern.pixels[pi]!, pg = this.pattern.pixels[pi + 1]!;
        const pb = this.pattern.pixels[pi + 2]!, pa = this.pattern.pixels[pi + 3]! / 255 * strength;

        const sr = data[idx]!, sg = data[idx + 1]!, sb = data[idx + 2]!, sa = data[idx + 3]!;
        data[idx] = Math.round(sr * (1 - pa) + pr * pa);
        data[idx + 1] = Math.round(sg * (1 - pa) + pg * pa);
        data[idx + 2] = Math.round(sb * (1 - pa) + pb * pa);
        data[idx + 3] = Math.min(255, Math.round(sa * (1 - pa) + 255 * pa));

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    layer.pixels.markDirty();
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
